"use client";
import React, {
  use,
  useActionState,
  useEffect,
  useState,
  useTransition,
} from "react";
import { useSearchParams } from "next/navigation";
import { getItemsInCategoriesAction, getMenuAction } from "@/app/actions";
import { ServicesPage } from "./servicesPage";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { ClientWrapperProps } from "@/dto/clientWrapper";
import { Category } from "@/dto/category";
import {
  getServiceItemsFromCache,
  setServiceItemsInCache,
  getMenuFromCache,
  setMenuInCache,
} from "@/utils/menuCacheUtils";

export function ServicesPageClientWrapper({
  merchant,
  itemTemplate,
  categories,
  paymentConfigPromise,
  preferencesPromise,
}: ClientWrapperProps) {
  const searchParams = useSearchParams();
  const [isTransitioning, startTransition] = useTransition();
  const [serviceItems, setServiceItems] = useState<Category[] | null>(
    getServiceItemsFromCache()
  );

  const [menuState, getMenu, isMenuLoading] = useActionState(getMenuAction, {
    data: getMenuFromCache(),
    error: null,
  });

  const [isLoading, setIsLoading] = useState<boolean>(!menuState.data);

  useEffect(() => {
    if (merchant && !menuState.data) {
      startTransition(() => {
        getMenu({ mid: merchant.id });
      });
    }
  }, [menuState.data]);

  useEffect(() => {
    if (categories && categories.length > 0 && merchant && !serviceItems) {
      startTransition(async () => {
        const result = await getItemsInCategoriesAction(null, {
          mid: merchant.id,
          categories,
          itemType: "SERVICE",
        });

        if (result.data) {
          setServiceItems(result.data);
          setServiceItemsInCache(result.data);
        }
      });
    } else if (categories && categories.length === 0 && !serviceItems) {
      setServiceItems([]);
      setServiceItemsInCache([]);
    }
  }, [categories, merchant.id, serviceItems]);

  const merchantPaymentConfig =
    paymentConfigPromise && use(paymentConfigPromise);

  const merchantPreferences = preferencesPromise && use(preferencesPromise);

  const merchantMeta = merchant.merchantMeta;
  const merchantColor =
    merchantMeta?.colorPalette?.color || cloverColorTheme.Main.primaryColor;
  const menu = menuState.data;

  useEffect(() => {
    if (menu) {
      setMenuInCache(menu);
    }
  }, [menu]);

  useEffect(() => {
    if (menu != null && !isMenuLoading && !isTransitioning) {
      setIsLoading(false);
    }
  }, [isMenuLoading, menu, isTransitioning]);

  return (
    <ServicesPage
      merchantName={merchant.name}
      merchantColor={merchantColor}
      searchParams={Object.fromEntries(searchParams.entries())}
      isCheckoutEnabled={merchant.isCheckoutEnabled}
      menu={menu}
      serviceCategories={serviceItems || []}
      itemTemplate={itemTemplate}
      merchantId={merchant.id}
      isAcceptingAppointments={isBusinessOpen(merchant)}
      merchantPreferences={merchantPreferences}
      merchantPaymentConfig={merchantPaymentConfig}
      isLoading={isLoading}
    />
  );
}
